import React, { useEffect, useMemo, useState } from 'react'
import Chart from 'react-apexcharts'

import { ChartEmpty, ChartShell } from './styled'

const getReducedMotion = () => (
  typeof window !== 'undefined'
  && typeof window.matchMedia === 'function'
  && window.matchMedia('(prefers-reduced-motion: reduce)').matches
)

export default function AnalyticsChart({
  activityPalette = false,
  ariaLabel,
  categories = [],
  dataLabels = false,
  distributed = false,
  formatValue = value => value,
  height = 300,
  horizontal = false,
  percentage = false,
  series = [],
  stacked = false,
  t,
  type = 'area',
}) {
  const [reducedMotion, setReducedMotion] = useState(getReducedMotion)

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return undefined
    const media = window.matchMedia('(prefers-reduced-motion: reduce)')
    const update = event => setReducedMotion(event.matches)

    if (media.addEventListener) media.addEventListener('change', update)
    else media.addListener(update)

    return () => {
      if (media.removeEventListener) media.removeEventListener('change', update)
      else media.removeListener(update)
    }
  }, [])

  const hasData = useMemo(() => series.some(item => (
    typeof item === 'number'
      ? Number.isFinite(item)
      : Array.isArray(item)
        ? item.length > 0
        : Array.isArray(item?.data) && item.data.length > 0
  )), [series])

  const options = useMemo(() => ({
    chart: {
      animations: {
        enabled: !reducedMotion,
        easing: 'easeinout',
        speed: 420,
        animateGradually: { enabled: !reducedMotion, delay: 40 },
        dynamicAnimation: { enabled: !reducedMotion, speed: 360 },
      },
      background: 'transparent',
      fontFamily: 'Poppins, sans-serif',
      foreColor: 'var(--analytics-chart-muted)',
      stacked,
      toolbar: { show: false },
      zoom: { enabled: false },
    },
    colors: activityPalette ? [
      'var(--analytics-activity-one)',
      'var(--analytics-activity-two)',
      'var(--analytics-activity-three)',
      'var(--analytics-activity-four)',
      'var(--analytics-activity-five)',
    ] : [
      'var(--analytics-chart-accent)',
      'var(--analytics-chart-secondary)',
      'var(--analytics-chart-tertiary)',
      'var(--analytics-chart-quaternary)',
    ],
    dataLabels: {
      enabled: dataLabels,
      formatter: (value, context) => type === 'donut'
        ? formatValue(context?.w?.config?.series?.[context.seriesIndex] ?? value)
        : percentage ? `${formatValue(value)}` : formatValue(value),
      style: { fontSize: '11px', fontWeight: 600 },
    },
    fill: type === 'area' ? {
      type: 'gradient',
      gradient: { shadeIntensity: 0.35, opacityFrom: 0.36, opacityTo: 0.03, stops: [0, 88, 100] },
    } : { opacity: type === 'bar' ? 0.92 : 1 },
    grid: {
      borderColor: 'var(--analytics-chart-grid)',
      padding: { left: horizontal ? 10 : 4, right: 14, top: 4, bottom: 0 },
      strokeDashArray: 4,
    },
    labels: categories,
    legend: {
      fontSize: '12px',
      fontWeight: 500,
      labels: { colors: 'var(--analytics-chart-muted)' },
      markers: { radius: 8 },
      position: type === 'donut' ? 'bottom' : 'top',
      horizontalAlign: 'left',
    },
    markers: {
      hover: { sizeOffset: 4 },
      size: type === 'line' || type === 'area' ? 3 : 0,
      strokeWidth: 0,
    },
    noData: { text: t('analytics_no_data_title') },
    plotOptions: {
      bar: {
        borderRadius: 4,
        borderRadiusApplication: 'end',
        borderRadiusWhenStacked: 'last',
        columnWidth: distributed ? '58%' : '62%',
        distributed,
        horizontal,
      },
      heatmap: {
        enableShades: true,
        shadeIntensity: 0.45,
        radius: 4,
        useFillColorAsStroke: false,
      },
      pie: {
        donut: {
          size: '70%',
          labels: {
            show: true,
            total: { show: true, label: t('analytics_total') },
          },
        },
      },
    },
    responsive: [{
      breakpoint: 620,
      options: {
        chart: { height: Math.min(height, 270) },
        dataLabels: { enabled: false },
        legend: { position: 'bottom', horizontalAlign: 'center' },
        xaxis: { labels: { rotate: -35, hideOverlappingLabels: true } },
      },
    }],
    stroke: {
      curve: 'smooth',
      lineCap: 'round',
      width: type === 'area' || type === 'line' ? 3 : 0,
    },
    theme: { mode: 'dark' },
    tooltip: {
      followCursor: true,
      shared: !['donut', 'heatmap'].includes(type),
      intersect: false,
      theme: 'dark',
      y: { formatter: value => formatValue(value) },
    },
    xaxis: {
      categories,
      axisBorder: { color: 'var(--analytics-chart-grid)' },
      axisTicks: { color: 'var(--analytics-chart-grid)' },
      labels: {
        hideOverlappingLabels: true,
        style: { colors: 'var(--analytics-chart-muted)', fontSize: '11px' },
        trim: true,
      },
      tooltip: { enabled: false },
    },
    yaxis: type === 'donut' ? undefined : {
      forceNiceScale: true,
      labels: {
        formatter: value => horizontal ? value : formatValue(value),
        maxWidth: horizontal ? 140 : 120,
        minWidth: horizontal ? 88 : 0,
        style: { colors: ['var(--analytics-chart-muted)'], fontSize: '11px' },
      },
      max: percentage ? 100 : undefined,
      min: 0,
    },
  }), [activityPalette, categories, dataLabels, distributed, formatValue, height, horizontal, percentage, reducedMotion, stacked, t, type])

  return (
    <ChartShell aria-label={ariaLabel} role="img">
      {hasData ? (
        <Chart height={height} options={options} series={series} type={type} width="100%" />
      ) : (
        <ChartEmpty>{t('analytics_no_data_title')}</ChartEmpty>
      )}
    </ChartShell>
  )
}
